<template>
  <div class="max-w-6xl p-6 mx-auto md:p-8">
    <!-- Page header -->
    <div>
      <h2 class="text-2xl font-semibold text-gray-800">Roles &amp; Permissions</h2>
      <p class="text-sm text-gray-500">
        Manage roles and control which modules each role can access
      </p>
    </div>

    <div class="mt-6 space-y-4">
      <!-- Roles card -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header class="flex items-start px-6 py-4 border-b border-gray-100">
          <span
            class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
          >
            groups
          </span>
          <div>
            <h3 class="text-base font-semibold text-gray-800">Roles</h3>
            <p class="text-sm text-gray-500">
              Select a role to view or edit its permissions
            </p>
          </div>
        </header>
        <div class="p-6">
          <div
            v-if="loadingRoles"
            class="flex items-center text-sm text-gray-500"
          >
            <i class="mr-2 text-lg material-icons-round animate-spin">loop</i>
            Loading roles...
          </div>
          <div v-else class="flex flex-wrap items-center gap-2">
            <button
              v-for="role in roles"
              :key="role.roleName"
              type="button"
              @click="selectRole(role)"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold border rounded-xl transition-colors duration-150"
              :class="
                activeRole.roleName === role.roleName
                  ? 'text-white bg-taxiye-500 border-taxiye-500 shadow-sm'
                  : 'text-gray-600 bg-white border-gray-200 hover:bg-taxiye-50 hover:text-taxiye-600'
              "
            >
              <span
                v-if="role.roleName === 'Super Admin'"
                class="mr-1.5 text-base material-icons-round"
              >
                shield
              </span>
              {{ role.roleName }}
            </button>

            <!-- Create role -->
            <div
              v-if="creatingNewRole"
              class="inline-flex items-center gap-2 p-1.5 ml-2 border border-taxiye-200 rounded-xl bg-taxiye-50"
            >
              <input
                type="text"
                v-model="newRole"
                @keyup.enter="createRole"
                placeholder="New role name..."
                class="block w-40 h-8 px-3 text-sm text-gray-800 border-gray-300 form-input rounded-lg focus:border-taxiye-300"
              />
              <button
                type="button"
                @click="createRole"
                :disabled="creatingRBAC || !newRole.trim()"
                class="inline-flex items-center h-8 px-3 text-sm font-semibold rounded-lg transition-colors duration-150"
                :class="
                  creatingRBAC || !newRole.trim()
                    ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                    : 'text-white bg-taxiye-500 hover:bg-taxiye-600'
                "
              >
                <i
                  class="mr-1 text-base material-icons-round"
                  :class="{ 'animate-spin': creatingRBAC }"
                >
                  {{ creatingRBAC ? "loop" : "check" }}
                </i>
                Done
              </button>
              <button
                type="button"
                @click="creatingNewRole = false"
                class="inline-flex items-center h-8 px-3 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg transition-colors duration-150 hover:bg-gray-100"
              >
                <i class="mr-1 text-base material-icons-round">close</i>
                Cancel
              </button>
            </div>
            <button
              v-else
              type="button"
              @click="creatingNewRole = true"
              class="inline-flex items-center h-10 px-4 ml-2 text-sm font-semibold text-taxiye-600 border border-dashed border-taxiye-300 rounded-xl transition-colors duration-150 hover:bg-taxiye-50"
            >
              <span class="mr-1 text-base material-icons-round">add</span>
              Create a new Role
            </button>
          </div>
        </div>
      </section>

      <!-- Permissions card -->
      <section class="bg-white border border-gray-200 rounded-2xl shadow-sm">
        <header
          class="flex flex-wrap items-center justify-between gap-4 px-6 py-4 border-b border-gray-100"
        >
          <div class="flex items-start">
            <span
              class="flex items-center justify-center flex-shrink-0 w-10 h-10 mr-4 text-taxiye-500 bg-taxiye-50 rounded-xl material-icons-round"
            >
              admin_panel_settings
            </span>
            <div>
              <h3 class="text-base font-semibold text-gray-800">
                Permissions
                <span v-if="activeRole.roleName" class="text-taxiye-600">
                  &mdash; {{ activeRole.roleName }}
                </span>
              </h3>
              <p class="text-sm text-gray-500">
                Choose which modules are accessible from the
                {{ activeRole.roleName }} role
              </p>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <span
              v-if="isSuperAdmin"
              class="inline-flex items-center px-2.5 py-0.5 text-xs font-semibold text-gray-500 bg-gray-100 rounded-full"
            >
              <span class="mr-1 text-sm material-icons-round">lock</span>
              Super Admin can't be modified
            </span>
            <button
              type="button"
              @click="saveRBAC"
              :disabled="isSuperAdmin || savingRBAC"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold rounded-xl shadow-sm transition-colors duration-150"
              :class="
                isSuperAdmin || savingRBAC
                  ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                  : 'text-white bg-taxiye-500 hover:bg-taxiye-600'
              "
            >
              <i
                class="mr-1.5 text-base material-icons-round"
                :class="{ 'animate-spin': savingRBAC }"
              >
                {{ savingRBAC ? "loop" : "save" }}
              </i>
              Save
            </button>
            <button
              type="button"
              @click="deleteRBAC"
              :disabled="isSuperAdmin || deletingRBAC"
              class="inline-flex items-center h-10 px-4 text-sm font-semibold border rounded-xl transition-colors duration-150"
              :class="
                isSuperAdmin || deletingRBAC
                  ? 'text-gray-400 bg-gray-100 border-gray-200 cursor-not-allowed'
                  : 'text-red-600 bg-white border-red-200 hover:bg-red-100'
              "
            >
              <i
                class="mr-1.5 text-base material-icons-round"
                :class="{ 'animate-spin': deletingRBAC }"
              >
                {{ deletingRBAC ? "loop" : "delete" }}
              </i>
              Delete Role
            </button>
          </div>
        </header>
        <div class="overflow-hidden">
          <div class="overflow-x-auto">
            <role-permissions
              :role-name="activeRole.roleName"
              v-model="activeRole.permissions"
            ></role-permissions>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import RolePermissions from "@/components/RolePermissions";

export default {
  components: {
    RolePermissions,
  },
  data() {
    return {
      roles: [],
      activeRole: "",

      creatingNewRole: false,
      newRole: "",

      savingRBAC: false,
      deletingRBAC: false,
      creatingRBAC: false,

      loadingRoles: false,
    };
  },
  created() {
    this.fetchRoles();
  },
  computed: {
    isSuperAdmin() {
      return this.activeRole.roleName === "Super Admin";
    },
  },
  methods: {
    fetchRoles() {
      this.loadingRoles = true;
      this.$misc
        .get("roles/")
        .then(({ data: { data } }) => {
          this.roles = data;
          this.activeRole = this.roles[0];
        })
        .finally(() => {
          this.loadingRoles = false;
        });
    },
    selectRole(role) {
      this.activeRole = role;
    },
    saveRBAC() {
      if (this.isSuperAdmin) return;
      this.savingRBAC = true;
      this.$misc
        .patch(`roles/${this.activeRole._id}`, this.activeRole)
        .then(() => {
          window.flash({
            type: "success",
            body: "Role updated successfully!",
          });
        })
        .catch(err => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.message &&
            err.response.data.message.includes("ValidationError")
          ) {
            window.flash({
              type: "error",
              body: err.response.data.message
                .split(":")
                .slice(1)
                .join(":")
                .split(",")
                .join("<br/>"),
            });
            console.log(err);
          } else {
            window.flash({
              type: "error",
              body: "Role couldn't be updated!",
            });
            console.log(err);
          }
        })
        .finally(() => (this.savingRBAC = false));
    },
    deleteRBAC() {
      if (this.isSuperAdmin) return;
      if (
        !window.confirm(
          `Delete the "${this.activeRole.roleName}" role? This can't be undone.`
        )
      )
        return;
      this.deletingRBAC = true;
      this.$misc
        .delete(`roles/${this.activeRole._id}`)
        .then(() => {
          window.flash({
            type: "success",
            body: "Role deleted successfully!",
          });
          this.fetchRoles();
        })
        .catch(err => {
          if (
            err.response &&
            err.response.data &&
            err.response.data.message &&
            err.response.data.message.includes("ValidationError")
          ) {
            window.flash({
              type: "error",
              body: err.response.data.message
                .split(":")
                .slice(1)
                .join(":")
                .split(",")
                .join("<br/>"),
            });
            console.log(err);
          } else {
            window.flash({
              type: "error",
              body: "Error: couldn't delete the role.",
            });
            console.log(err);
          }
        })
        .finally(() => (this.deletingRBAC = false));
    },
    createRole() {
      if (!this.newRole.trim()) return;
      this.creatingRBAC = true;
      this.$misc
        .post("roles/", {
          roleName: this.newRole,
        })
        .then(() => {
          this.creatingNewRole = false;
          this.newRole = "";
          this.fetchRoles();
        })
        .catch(err => console.log(err))
        .finally(() => (this.creatingRBAC = false));
    },
  },
};
</script>
